import { useState } from "react";
import { reportWriteFailure } from "@/lib/reportWriteFailure";
import { Link } from "wouter";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import Icon from "@/components/mnf/Icon";
import { GlassCard, Btn, Avatar } from "@/components/mnf/UI";
import { useStoreQuery, queries } from "@/lib/store";
import { api } from "../../../convex/_generated/api";

/**
 * /coach/review — coach moderation queue.
 *
 * Lists every recipe that is `public: true, pendingReview: true` and
 * lets the coach approve or reject. Backend (convex/coachReview.ts)
 * enforces role=coach|admin (with a dev-bypass fallthrough). This page
 * also gates the UI to coaches/admins so non-coaches see a friendly
 * "this is for coaches" empty state instead of a server error.
 */
export default function CoachReview() {
  const me = useStoreQuery(queries.currentUser);
  const isCoach = !!me && (me.role === "coach" || me.role === "admin");

  // Skip the query when the viewer isn't a coach so we don't spam an
  // unauthorized request.
  const queue = useConvexQuery(
    api.coachReview.pendingReviewRecipes,
    isCoach ? {} : "skip",
  );
  const approveRecipe = useConvexMutation(api.coachReview.approveRecipe);
  const rejectRecipe = useConvexMutation(api.coachReview.rejectRecipe);
  const reportedComments = useConvexQuery(
    api.coachReview.reportedComments,
    isCoach ? {} : "skip",
  );
  const approveComment = useConvexMutation(api.coachReview.approveComment);
  const removeComment = useConvexMutation(api.coachReview.removeComment);

  const [busyId, setBusyId] = useState<string | null>(null);

  const handle = async (
    recipeId: string,
    action: "approve" | "reject",
  ) => {
    if (busyId) return;
    setBusyId(recipeId);
    try {
      if (action === "approve") await approveRecipe({ recipeId });
      else await rejectRecipe({ recipeId });
    } catch (err: unknown) {
      // try/finally with no catch: the spinner stopped and the row stayed in
      // the queue, so Kiley could not tell a failed approval from one that had
      // not re-rendered — and a sister's recipe sat unreviewed.
      reportWriteFailure(
        "coach-review", `${action}ing a recipe`, err,
        action === "approve" ? "We couldn't approve that" : "We couldn't reject that",
        "The recipe is still in your queue. Check your connection and try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  if (!isCoach) {
    return (
      <div className="px-5 pt-8 pb-10 space-y-3">
        <h1 className="mnf-serif" style={{ fontSize: 28, fontWeight: 500, color: "var(--mnf-fd)" }}>
          Coach review
        </h1>
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          This page is for coaches. Sister recipes wait here while Kiley
          signs off, and you'll see them on her side.
        </p>
        <Link href="/nourish">
          <Btn variant="primary" size="sm">Back to Nourish</Btn>
        </Link>
      </div>
    );
  }

  return (
    <div className="px-5 pt-3 pb-10 space-y-4">
      <div>
        <p className="mnf-eyebrow">Coach · review queue</p>
        <h1 className="mnf-serif" style={{ fontSize: 30, fontWeight: 500, color: "var(--mnf-fd)" }}>
          Pending recipes
        </h1>
        <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", marginTop: 4 }}>
          Approve to send to Trending. Reject hides from public — sister can edit and resubmit.
        </p>
      </div>

      {queue === undefined ? (
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>Loading queue…</p>
      ) : queue.length === 0 ? (
        <GlassCard padding={16}>
          <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
            Inbox zero. No recipes waiting on review.
          </p>
        </GlassCard>
      ) : (
        <ul className="space-y-3">
          {queue.map((r) => (
            <ReviewItem
              key={r._id}
              recipeId={r.id}
              authorId={r.authorId}
              title={r.title}
              description={r.description}
              servings={r.servings}
              durationMin={r.durationMin}
              ingredientCount={r.ingredients.length}
              busy={busyId === r.id}
              onApprove={() => handle(r.id, "approve")}
              onReject={() => handle(r.id, "reject")}
            />
          ))}
        </ul>
      )}

      {/* Reported comments queue */}
      <div className="pt-4">
        <p className="mnf-eyebrow mt-2">Reported comments</p>
        {reportedComments === undefined ? (
          <p className="mnf-body" style={{ color: "var(--mnf-tg)", fontSize: 13 }}>Loading…</p>
        ) : reportedComments.length === 0 ? (
          <GlassCard padding={14}>
            <p className="mnf-body" style={{ color: "var(--mnf-tg)", fontSize: 13 }}>
              No reports.
            </p>
          </GlassCard>
        ) : (
          <ul className="space-y-2">
            {reportedComments.map((c) => (
              <GlassCard key={c._id} padding={12}>
                <li className="space-y-1.5">
                  <p
                    className="mnf-body"
                    style={{ fontSize: 13, color: "var(--mnf-tx)", whiteSpace: "pre-wrap" }}
                  >
                    {c.body}
                  </p>
                  <p className="mnf-eyebrow" style={{ fontSize: 10, color: "var(--mnf-tg)" }}>
                    Reported by {(c.reportedBy ?? []).length} sister{(c.reportedBy ?? []).length === 1 ? "" : "s"} · on recipe {c.recipeId}
                  </p>
                  <div className="flex gap-2">
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={() => { void removeComment({ commentId: c._id }).catch((err: unknown) => { reportWriteFailure("coach-review", "removing a comment", err, "We couldn't remove that comment", "It's still visible. Check your connection and try again."); }); }}
                      data-testid={`button-remove-comment-${c._id}`}
                    >
                      <Icon name="delete" size={14} /> Remove
                    </Btn>
                    <Btn
                      variant="primary"
                      size="sm"
                      onClick={() => { void approveComment({ commentId: c._id }).catch((err: unknown) => { reportWriteFailure("coach-review", "approving a comment", err, "We couldn't allow that comment", "It's still held for review. Check your connection and try again."); }); }}
                      data-testid={`button-approve-comment-${c._id}`}
                    >
                      <Icon name="check" size={14} /> Allow
                    </Btn>
                  </div>
                </li>
              </GlassCard>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ReviewItem({
  recipeId,
  authorId,
  title,
  description,
  servings,
  durationMin,
  ingredientCount,
  busy,
  onApprove,
  onReject,
}: {
  recipeId: string;
  authorId: string;
  title: string;
  description: string;
  servings: number;
  durationMin: number;
  ingredientCount: number;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const author = useConvexQuery(api.users.profileByDomainId, { domainId: authorId });
  return (
    <GlassCard padding={14}>
      <li className="space-y-2">
        <div className="flex items-center gap-2">
          <Avatar
            initials={author?.initials ?? "S"}
            size={28}
            ringColor={author?.isCoach ? "rgba(160,120,32,0.55)" : undefined}
          />
          <div className="flex-1 min-w-0">
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 10 }}>
              {author?.name ?? "A sister"}
            </p>
            <Link
              href={`/nourish/${recipeId}`}
              className="mnf-serif no-underline"
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: "var(--mnf-fd)",
                lineHeight: 1.3,
              }}
            >
              {title}
            </Link>
          </div>
        </div>

        <p
          className="mnf-body"
          style={{ fontSize: 13, color: "var(--mnf-tx)", lineHeight: 1.45 }}
        >
          {description}
        </p>

        <div
          className="mnf-eyebrow flex items-center gap-3"
          style={{ fontSize: 10, color: "var(--mnf-tg)" }}
        >
          <span><Icon name="restaurant" size={12} /> {servings} serv</span>
          <span><Icon name="schedule" size={12} /> {durationMin}m</span>
          <span><Icon name="checklist" size={12} /> {ingredientCount} ing</span>
        </div>

        <div className="flex gap-2 pt-1">
          <Btn
            variant="ghost"
            size="sm"
            onClick={onReject}
            disabled={busy}
            data-testid={`button-reject-${recipeId}`}
          >
            <Icon name="close" size={14} /> Reject
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            onClick={onApprove}
            disabled={busy}
            data-testid={`button-approve-${recipeId}`}
          >
            <Icon name="check" size={14} /> Approve
          </Btn>
        </div>
      </li>
    </GlassCard>
  );
}
